import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  Sparkles,
  Search,
  BookOpen,
  ArrowLeft,
  ArrowRight,
  Clock,
  Calendar,
  ChevronRight,
  Share2,
  Check,
  Tag,
  ArrowUpRight,
  Cpu,
  TrendingUp,
  ShieldCheck,
  FileText,
  X,
  User,
  ExternalLink
} from "lucide-react";

const ARTICLES = [
  {
    id: "how-to-write-content-that-ranks-on-google-and-improves-ai-visibility",
    slug: "/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/",
    title: "How to Write Content That Ranks on Google and Improves AI Visibility",
    subtitle: "Good content should help your target audience make a decision, not just explain how great your product is. Discover the 10 principles to create content that ranks on Google and improves AI visibility across ChatGPT, Google AI Overviews, Gemini, and Perplexity.",
    category: "AEO/GEO , AI Visibility",
    date: "Nov 03, 2026",
    readTime: "11 min read",
    badge: "10 Content Strategy Principles",
    featured: true,
    summary:
      "Good content should help your target audience make a decision, not just explain how great your product is. To improve your SEO rankings and AI visibility across ChatGPT, Google AI Overviews, Gemini and Perplexity, create content around real user problems, provide practical insights, explain possible solutions and support your information with evidence.",
    content: [
      {
        heading: "Key Takeaway: Write Content That Helps Users Make a Decision",
        text: `Good content should help your target audience make a decision, not just explain how great your product is. To improve your SEO rankings and AI visibility across platforms like ChatGPT, Google AI Overviews, Gemini and Perplexity, create content around real user problems, provide practical insights, explain possible solutions and support your information with evidence.`
      },
      {
        heading: "1. Choose the Right Topic Based on User Intent, Not Product Priorities",
        text: `Don't pick topics simply because your product team wants to promote a feature or a tool shows search volume. Choose topics based on the questions, challenges and decisions your target audience faces across Google Search Console, Reddit, Quora, reviews, and sales calls.`
      },
      {
        heading: "2. Get Straight to the Point: Don't Make Readers Search for Answers",
        text: `Start with the actual problem and address it directly. Write as much as the topic requires, not as much as an arbitrary SEO word-count recommendation suggests.`
      },
      {
        heading: "3. Add Your Own Insights to the Content",
        text: `Include practical observations from your industry, common mistakes, and lessons from real customer experiences so your article offers value that generic content cannot replicate.`
      },
      {
        heading: "4. Make Your Content Problem-Solving, Not Product-Selling",
        text: `Address the problems businesses face first, explain different approaches, and anticipate related follow-up questions to guide the reader through the decision-making process.`
      },
      {
        heading: "5. Explain Different Scenarios and Possible Solutions",
        text: `Break topics into scenarios (e.g., small business, growing business, multi-location business, large organisation) so readers know which solution applies to their situation.`
      },
      {
        heading: "6. Use Comparative Content Wherever It Helps the Reader",
        text: `Use clear, structured comparison tables and side-by-side evaluations when they make trade-offs easier to understand.`
      },
      {
        heading: "7. Support Your Content With Evidence",
        text: `Back your statements with customer case studies, real reviews, original research, statistics, and product demonstrations.`
      },
      {
        heading: "8. Explain Where Your Product Fits Into the Solution",
        text: `Introduce your product naturally as a relevant workflow solution supported by substantiable comparisons and customer proof.`
      },
      {
        heading: "9. Follow a Simple Content Structure",
        text: `Use a flexible, intent-driven structure: A. Introduction, B. Main problem, C. Possible solutions, D. Comparisons and insights, E. Product relevance, and F. Conclusion.`
      },
      {
        heading: "10. Include Expert Insights and a Credible Author Profile",
        text: `Reflect real knowledge with genuine subject-matter expertise and an accurate author profile explaining who wrote the article and their relevant experience.`
      }
    ]
  },
  {
    id: "how-to-do-keyword-research-for-ai-visibility",
    slug: "/blog/how-to-do-keyword-research-for-ai-visibility/",
    title: "How to Do Keyword Research for AI Visibility: A Customer-Intent-First Approach",
    subtitle: "Traditional keyword tools show search volume, but not what real customers struggle with before buying. Learn how to build a Question Memory using GSC, Reddit, customer reviews, and sales conversations to get cited in ChatGPT, Claude, and Google AI Overviews.",
    category: "AEO/GEO , AI Visibility",
    date: "Nov 02, 2026",
    readTime: "10 min read",
    badge: "Customer-Intent Framework",
    featured: false,
    summary:
      "Traditional keyword research tells you what people search for, but not what problems they struggle with before buying. Learn an 11-step customer-intent-first approach to build a Question Memory and maximize your AI visibility across ChatGPT, Claude, and Google.",
    content: [
      {
        heading: "Why Customer Intent Matters More Than Search Volume for AI Visibility",
        text: `Traditional keyword research tells you what people search for. But does it tell you what customers are struggling with, what they compare before buying, or what stops them from choosing a product?

Not always.

With AI search, people ask detailed questions, explain their problems and compare solutions based on their specific requirements. Finding these questions requires more than looking at search volume in SEO tools.

You need to understand what your customers are actually asking.`
      },
      {
        heading: "1. Start With Your Existing Google Search Console Data",
        text: `Before looking for new keywords, understand what people are already searching for to find your business. Filter queries in Google Search Console by product keywords and handpick queries with genuine business relevance.`
      },
      {
        heading: "2. Find Real Customer Questions on Reddit and Quora",
        text: `Search for discussions comparing products, asking for recommendations, debating pricing, and troubleshooting common problems. Keep all questions in a spreadsheet called your Question Memory.`
      },
      {
        heading: "3. Study Customer and Competitor Reviews",
        text: `Explore G2, Capterra, Trustpilot, and Google Reviews. Look at negative reviews to identify common complaints (difficult setup, poor support, integration issues) so your product can solve them.`
      },
      {
        heading: "4. Collect Questions From Sales Calls, Demos and Support Tickets",
        text: `Your existing customers are the best source. Extract objections, migration questions, and pricing doubts from call transcripts and support tickets.`
      },
      {
        heading: "5. Explore Google Search Suggestions and Related Questions",
        text: `Collect Google Autocomplete suggestions, People Also Ask boxes, and related search snippets to see how people phrase questions naturally.`
      },
      {
        heading: "6. Find Questions in Industry Forums",
        text: `Engage with niche communities and forums where buyers discuss software choices and implementation challenges that never appear in standard SEO tools.`
      },
      {
        heading: "7. Explore Questions in YouTube Comments",
        text: `Check the comment sections under popular tutorial and product comparison videos to extract questions the videos left unanswered.`
      },
      {
        heading: "8. Explore Influencer Videos and Social Media Discussions",
        text: `Read comments on LinkedIn, Instagram Reels, and industry creator posts to spot common misconceptions, budget limits, and buying preferences.`
      },
      {
        heading: "9. Turn Your Question Memory Into Keyword and Prompt Opportunities",
        text: `Group questions into topics and classify them by customer intent: Problem awareness, Solution research, Product comparison, and Purchase decision.`
      },
      {
        heading: "10. Why Question Memory Is More Valuable Than a Keyword List Alone",
        text: `Traditional keyword tools tell you what people search for. Your Question Memory tells you what problems they want solved. You need both to succeed.`
      },
      {
        heading: "11. How to Use Question Memory for SEO and AI Search",
        text: `Use traditional SEO to validate search volume and intent. Use AI search optimization to answer the entire customer journey with clear explanations, facts, and prompt testing.`
      }
    ]
  },
  {
    id: "how-to-audit-a-website-for-ai-visibility",
    slug: "/how-to-audit-a-website-for-ai-visibility/",
    title: "How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude?",
    subtitle: "Is your website AI-ready? Audit it yourself with these 10 practical checks covering crawler access, indexability, JavaScript rendering parity, customer fan-out questions, and citations.",
    category: "AEO/GEO , AI Visibility",
    date: "Oct 22, 2026",
    readTime: "12 min read",
    badge: "10-Point Technical Checklist",
    featured: false,
    summary:
      "Is your website ready to be discovered, understood and cited by AI platforms such as ChatGPT, Google AI Overviews, Claude and Perplexity? Audit your website yourself with this 10-point technical checklist and fan-out query framework.",
    content: [
      {
        heading: "Is Your Website AI-Ready? Audit It Yourself With These 10 Checks",
        text: `Several factors affect how AI search systems access your website, understand your content and identify useful information to include in their answers. These include technical accessibility, indexability, content quality, business information and the evidence supporting your claims.

In this guide, you'll learn how to perform 10 important AI visibility audit checks on your own website.`
      },
      {
        heading: "1. Check Whether AI Search Bots Can Access Your Website",
        text: `Check robots.txt for OAI-SearchBot, Googlebot, Claude-SearchBot, and PerplexityBot. Check CDN/WAF firewall rules for 403 or 429 errors, CAPTCHA challenges, and server logs for crawler requests.`
      },
      {
        heading: "2. Check Whether Your Important Pages Are Indexed",
        text: `Review Google Search Console for product, pricing, comparison, and case study pages. Check for noindex directives, robots disallows, and canonical mismatches.`
      },
      {
        heading: "3. Check Whether Content Is Accessible Without JavaScript Problems",
        text: `Compare raw HTML against rendered DOM. Ensure critical pricing, specifications, comparison tables, and FAQs are not locked behind client-side rendering.`
      },
      {
        heading: "4. Check Whether Your Content Answers Real Customer Questions",
        text: `Map buying journey fan-out questions (problem, solution, comparison, pricing, limitations) into a 3-tier gap matrix: Fully covered, Partially covered, and Not covered.`
      },
      {
        heading: "5. Check Whether Your Content Is Easy to Understand and Cite",
        text: `Provide direct answers first (AEO inverted pyramid), use descriptive headings, format complex data into tables, and eliminate vague fluff.`
      },
      {
        heading: "6. Check Your Canonical Tags, Redirects and Internal Links",
        text: `Ensure valid self-referencing canonicals, eliminate 301 redirect chains, and link supporting articles directly to core commercial pages.`
      },
      {
        heading: "7. Check Whether Search Engines Can Understand Your Business",
        text: `Disambiguate corporate entity with an About page, author credentials, social links, and Organization, Product, and Article schema.`
      },
      {
        heading: "8. Check Whether Your Claims Are Supported by Evidence",
        text: `Back commercial claims with customer case studies, measurable metrics, methodology transparency, and transparent product limitations.`
      },
      {
        heading: "9. Check Which Competitors AI Platforms Mention Instead of You",
        text: `Run repeatable fan-out prompt testing across ChatGPT Search, Google AI Overviews, Claude, and Perplexity to analyze cited competitor pages.`
      },
      {
        heading: "10. Measure AI Visibility Before and After Making Changes",
        text: `Track 5 separate indicators: AI bot requests (logs), Google AI impressions (GSC), Bing citations, GA4 AI referral traffic, and leads.`
      }
    ]
  },
  {
    id: "how-to-measure-ai-search-visibility-for-free",
    slug: "/blog/how-to-measure-ai-search-visibility-for-free/",
    title: "How to Measure AI Search Visibility for Free (Without Paid Tools)",
    subtitle: "Step-by-step technical framework to track AI crawlers, Google AI impressions, Bing citations, and GA4 AI referral traffic without paying for premium tools.",
    category: "AEO/GEO , AI Visibility",
    date: "Oct 14, 2026",
    readTime: "8 min read",
    badge: "Free Tactical Playbook",
    featured: false,
    summary:
      "If you have the budget to purchase AI visibility tools like Profound or Scrunch AI, this article may not be for you. Discover how SEO professionals and business owners can measure AI search performance using Google Search Console, Bing Webmaster Tools, server log files, GA4, and Microsoft Clarity.",
    content: [
      {
        heading: "How to Measure AI Search Visibility for Free (Without Paid Tools)",
        text: `If you have the budget to purchase AI visibility tools like Profound or Scrunch AI, this article may not be for you.

This guide is for SEO professionals and business owners who want to measure AI search performance without paying for premium tools.

You can start with five data sources:
• Google Search Console — AI Performance
• Bing Webmaster Tools — AI Performance
• Server log files
• Google Analytics 4 (GA4)
• Microsoft Clarity

The goal is simple: understand whether AI bots are accessing your pages, whether your content is appearing in AI search, and whether visitors are coming to your website from AI platforms.`
      },
      {
        heading: "Step 1: Select Your Important Pages",
        text: `Don't track every URL on your website. It creates too much data and makes analysis difficult.

Start with your most important pages:
• Product and service pages
• Comparison pages
• High-intent landing pages
• Blogs that target important business questions

Use the same list of URLs throughout your analysis.`
      },
      {
        heading: "Step 2: Collect Google Search Console Data",
        text: `Open Google Search Console and look for the Generative AI performance report, if available for your website.

Export the available data for your selected period and record AI impressions by page and week.

This helps you understand how your pages perform in Google's supported AI search experiences.`
      },
      {
        heading: "Step 3: Collect Bing Webmaster Tools Data",
        text: `Open Bing Webmaster Tools and access the AI Performance report.

Export the available citation data and organize it by page and week.

This helps you understand which pages are cited in supported AI experiences, including Microsoft Copilot.

Note: Google AI impressions and Bing citations are different metrics. Keep them separate.`
      },
      {
        heading: "Step 4: Analyze Server Log Files",
        text: `Download your server or CDN logs for the same period.

Identify relevant AI search and retrieval bots, then group their requests by page and week.

For example, OpenAI uses different crawlers for different purposes:
• OAI-SearchBot helps discover content for ChatGPT search.
• GPTBot is associated with crawling for potential model training.
• ChatGPT-User is used for certain user-triggered fetches.

If your goal is to measure AI search activity, focus on the relevant search and retrieval bots. Keep training-related crawling and user-triggered fetches separate.

Also, separate successful page responses from failed requests and remove irrelevant URLs.

Your output should show how many times relevant AI crawlers requested each important page during each week.

Remember: A bot request means that a page was fetched. It does not mean the page was cited or shown to a user.`
      },
      {
        heading: "Step 5: Collect AI Referral Traffic From GA4",
        text: `Open GA4 and go to Reports → Acquisition → Traffic acquisition.

Identify traffic from AI platforms such as ChatGPT and Claude, along with other sources where referral information is available.

Record the following by landing page and week:
• Sessions
• Engaged sessions
• Conversions

This helps you understand whether AI platforms are sending visitors to your website.

Some AI-assisted visits may appear as direct traffic, so GA4 will not capture every visit originating from an AI interaction.`
      },
      {
        heading: "Step 6: Use Microsoft Clarity to Understand Visitor Behaviour",
        text: `Use Microsoft Clarity to analyze visitors who arrive through identifiable AI referrals.

Check which pages they visit, how far they scroll and whether they interact with important elements such as enquiry forms.

This helps you understand what visitors do after reaching your website.

Clarity measures visitor behaviour, not AI citations.`
      },
      {
        heading: "Step 7: Combine the Data in One Spreadsheet",
        text: `Choose a fixed reporting period, such as three months, and divide it into weekly intervals.

Create a spreadsheet with these columns:
Page URL | Week | AI bot requests | Google AI impressions | Bing citations | AI referral sessions

/seo-services/ | Week 1 | 35 | 120 | 8 | 12
/seo-services/ | Week 2 | 48 | 150 | 12 | 18
/seo-audit/ | Week 1 | 5 | 40 | 1 | 3

The figures above are examples, not actual performance data.

Keep the metrics separate. They measure different stages of AI search performance and should not be added together into one score.`
      },
      {
        heading: "Step 8: Understand What the Data Is Telling You",
        text: `Before comparing the numbers, you need to understand what each data source means.

Each tool tells you something different about your website.

Server log files = Crawled: An AI bot visited and requested your page. This does not mean your page was cited.
Google Search Console AI Performance = AI visibility: Your page received impressions in Google's reported AI search experiences.
Bing Webmaster Tools AI Performance = Citations: Your page was cited in the AI experiences covered by Bing's report.
GA4 = Clicks and visits: People clicked through from an AI platform and visited your website, where the referral could be identified.
Microsoft Clarity = Visitor behaviour: You can see how visitors interact with your website after they arrive.

In simple terms:
Crawled → Visible or cited → Clicked → Visited your website

These are different signals. One does not guarantee the next.

How to use these signals:
• Crawled High + Visible/Cited High: AI bots are requesting your pages, and your content is receiving visibility or citations in the reports. Action: Maintain the pages, update the content and improve internal links.
• Crawled High + Visible/Cited Low: AI bots are visiting your pages, but reported AI visibility is low. Action: Review whether your content answers user questions clearly and offers useful, original information.
• Crawled Low + Visible/Cited High: Your pages are receiving reported visibility despite few recent bot requests in your logs. Action: Check the reporting dates, crawler coverage and whether cached content or earlier crawling explains the result.
• Crawled Low + Visible/Cited Low: You have little recorded crawler activity and little reported AI visibility. Action: Check robots.txt, page access, rendering, indexability and internal links.

High and low are relative to your own website and the period you are measuring. There is no universal number that defines high AI visibility.

Important: Google AI impressions and Bing citations are not the same metric. Keep them separate in your spreadsheet. Use each report to understand the AI visibility it actually measures.
Also, do not assume that more bot requests automatically mean more citations. The purpose of this analysis is to identify patterns and understand which pages need improvement.`
      },
      {
        heading: "Step 9: Track Performance for Three Months",
        text: `Repeat the same process every week for approximately three months.

Compare changes in:
• AI bot activity
• Google AI impressions
• Bing citations
• AI referral sessions
• Conversions from identifiable AI referrals

Record when you make important technical or content changes.

If visibility improves consistently after an update, investigate whether the change contributed to the improvement. Other factors, including platform updates and competitor activity, may also affect results.`
      },
      {
        heading: "Final Thoughts",
        text: `You don't need premium AI visibility tools to start measuring your website's AI search performance.

Use Google Search Console, Bing Webmaster Tools, server logs, GA4 and Microsoft Clarity to understand crawler access, AI visibility and visitor behaviour.

The goal is to connect these signals and identify which pages need attention.

If you need help identifying technical issues, content gaps and opportunities to improve AI search visibility, order an AI visibility audit: https://narendradigital.in/ai-visibility-audit/`
      }
    ]
  },
  {
    id: "why-do-i-rank-on-google-but-dont-appear-in-chatgpt",
    slug: "/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/",
    title: "Why Do I Rank on Google but Don't Appear in ChatGPT?",
    subtitle: "Ranking on Google and being visible in AI search are not exactly the same thing. Discover why competitors appear in ChatGPT while you don't, how OAI-SearchBot works, the mechanics of fan-out queries, and the 6-step AI visibility audit protocol.",
    category: "AEO/GEO , AI Visibility",
    date: "Oct 07, 2026",
    readTime: "9 min read",
    badge: "Signature Strategic Guide",
    featured: false,
    summary:
      "You rank on Google. Your website has good pages. You have done SEO. But then you search for your business in ChatGPT and your competitor appears while you don't. Here is why it happens and how to fix it with Hybrid Engine Optimization (HEO).",
    content: [
      {
        heading: "Why Do I Rank on Google but Don't Appear in ChatGPT?",
        text: `You rank on Google.

Your website has good pages.

You have done SEO.

But then you search for your business or product in ChatGPT and...

Your competitor appears. You don't.

This is becoming a common problem.

The reason is simple:

Ranking on Google and being visible in AI search are not exactly the same thing.

Google and AI-powered search systems may use different signals to find, understand and select information.

So if your website is only built to rank for keywords, you may be missing some important signals that AI systems need to understand your business.

I usually look at this through three major areas.`
      },
      {
        heading: "1. Your website may be blocking AI crawlers",
        text: `This is one of the first things I check.

Your website can be perfectly visible to Google but still have restrictions that affect AI search discovery.

For example, OpenAI uses a crawler called OAI-SearchBot for ChatGPT search. OpenAI specifically recommends allowing OAI-SearchBot if you want your website content to be discovered and included in ChatGPT search results.

So the first thing to check is:

Your robots.txt

Look for rules that may block AI crawlers.

For example:
User-agent: OAI-SearchBot
Disallow: /

If something like this exists intentionally, your website may not be available for ChatGPT search.

But robots.txt is not the only thing to check.

You should also check:
• HTTP status codes
• 403/401 responses
• CDN and WAF restrictions
• Bot protection
• Cloudflare rules
• Login requirements
• JavaScript challenges
• Noindex directives
• Canonical tags
• XML sitemaps
• Crawlable internal links

OpenAI also notes that firewalls, CDNs, bot mitigation, authentication and other access controls can prevent its crawlers from accessing a page.

So ask yourself:
Can an AI crawler actually reach and read my important pages?

If the answer is no, content optimization will not solve the problem.`
      },
      {
        heading: "2. Your important content may depend too much on JavaScript",
        text: `This is another area I check during an AI visibility audit.

Modern websites are often built using React, Next.js, Angular, JavaScript-heavy applications and other frameworks.

There is nothing wrong with using these technologies.

The problem starts when the important information only becomes available after client-side rendering or other browser-side actions.

Your most important information should be easy for crawlers to access.

For example:
• What does your company do?
• What products do you sell?
• Who is the product for?
• What problems does it solve?
• What are the important features?
• What industries do you serve?
• What locations do you serve?
• What makes you different?
• What questions do customers ask?

If this information is hidden behind complicated rendering, interaction, API calls or other client-side processes, you are creating unnecessary friction for search systems.

Google has supported JavaScript rendering for many years, so this is not a simple case of “JavaScript is bad for SEO.”

The better approach is:
“Do not make critical information dependent on JavaScript if you can make it directly available in the page's HTML.”

This makes your content easier to crawl, understand and process across different search environments.`
      },
      {
        heading: "3. You may be targeting keywords instead of user intent",
        text: `This is probably the biggest content problem I see.

Traditional SEO often starts with:
Keyword → Page → Ranking

AI search changes the way we need to think.

Users don't always ask AI systems using the exact keyword you are targeting.

They ask complete questions.

For example, you may target:
“CRM software”

But your potential customer may ask:
“What is the best CRM for a small sales team with 5 people?”

Then they may ask:
“Which CRM is affordable for a startup?”

Then:
“What should I check before choosing a CRM?”

Then:
“HubSpot vs Salesforce — which is easier for a small company?”

These are fan-out queries.

One main question creates many related questions.

AI-powered search experiences are increasingly designed around understanding the user's intent and providing synthesized answers rather than simply returning a list of pages.

So your content should not only answer:
“What keyword do I want to rank for?”

It should answer:
“What questions will my customer ask before, during and after making this decision?”`
      },
      {
        heading: "What are Fan-Out Queries?",
        text: `Imagine someone searches:
“Best project management software for a small company.”

That is only the beginning.

Their next questions could be:
• Which software is easiest to use?
• Which one is cheapest?
• Does it support automation?
• Does it integrate with Slack?
• Does it have time tracking?
• Which one is better for remote teams?
• Asana vs Monday — which is better?
• What are the disadvantages?
• How much does it cost?
• Can I migrate from another tool?
• Is it suitable for a 10-person company?

Your website should help answer these questions.

This does not mean creating 50 thin articles.

It means creating useful content that covers the decision journey.`
      },
      {
        heading: "There Are Other Signals You Should Not Ignore",
        text: `The three areas above are the first things I would investigate.

But AI visibility is not controlled by three factors alone.

There are several other problems that can reduce your chances of being discovered, understood or cited.

4. Your content looks exactly like everyone else's
If ten competitors say: “We provide innovative solutions to help businesses grow.”
You are not giving search systems much useful information.

AI systems need useful, specific information.
Instead of saying: “We are a leading HR software company.”
Explain: “Our HR platform helps Indian companies automate payroll, PF, ESI and employee attendance for teams between 50 and 5,000 employees.”
Specific information is easier to understand than marketing language.

5. Your entity is not consistent across the internet
Your website is not the only place where your business exists. Search engines and AI systems can find information about your company from LinkedIn, review websites, news, directories, Reddit, YouTube, and partner websites.
Your website tells AI what you say about yourself. The rest of the internet helps confirm whether other sources agree with you.

6. Your website may not be technically strong enough
Check indexability, 200 status, correct canonical, visible content without interaction, internal links, clean sitemap, structured data, speed, and mobile accessibility. Before trying advanced AEO or GEO tactics, fix the foundation.

7. Your website may answer questions, but not make the answer easy to use
AI systems need to understand information quickly. Compare marketing fluff with direct, atomic clarity:
Version 1: “Our innovative, next-generation platform provides businesses with a comprehensive and powerful ecosystem designed to enable organizations to achieve their digital transformation goals.”
Version 2: “Our platform helps HR teams manage payroll, attendance, leave and employee records from one system.”
Version 2 is far easier to understand. Write for humans first, but make the information clear enough that machines can understand it too.`
      },
      {
        heading: "So How Do You Fix It?",
        text: `I would not start by publishing 100 new blog posts. Start with an AI visibility audit.

Step 1: Check access
Check: robots.txt → OAI-SearchBot → other relevant crawlers → WAF/CDN → HTTP status → noindex. Make sure important pages can actually be accessed. OpenAI specifically states that allowing OAI-SearchBot helps make website content eligible for discovery in ChatGPT search.

Step 2: Check the technical foundation
Review: Indexability → Canonicals → Sitemaps → Internal links → Rendering → Structured data → Page speed.

Step 3: Understand your customer's questions
Don't start with only keywords. Start with: What is the customer trying to achieve? Then map the questions around that goal.

Step 4: Build content around the fan-out
One topic should answer the main question and the important follow-up questions. Don't create content just because a keyword has search volume. Create it because your customer needs the answer.

Step 5: Build a consistent entity
Make sure your company name, products, services, locations, people, categories, and descriptions are consistent across important sources.

Step 6: Test AI visibility
Don't assume your work is successful because you implemented schema or published ten articles. Actually test questions your customers ask. Record whether your company was mentioned, your website cited, competitor mentioned instead, information accuracy, and what source the AI used.`
      },
      {
        heading: "SEO + AI Visibility Should Not Be Two Separate Strategies",
        text: `This is where I believe many companies are going wrong.

You don't need an SEO strategy and then another completely separate AEO/GEO strategy.

You need a strategy that makes your website:
Crawlable + Understandable + Relevant + Useful + Consistent
for both traditional search and AI-powered search.

That is the thinking behind Hybrid Engine Optimization (HEO).

The goal is not: “How do I rank on ChatGPT?”
The better question is: “How do I make my business easy for both search engines and AI systems to discover, understand and confidently recommend?”

Google rankings still matter. Technical SEO still matters. Content still matters.
But the content now needs to answer the real questions behind the keyword.
And your website needs to provide enough clear information for search engines and AI systems to understand what you actually do.

Don't create content for Google.
Don't create content for ChatGPT.
Create the best possible answer for the person who is searching.
Then make sure both search engines and AI systems can access, understand and trust that answer.`
      }
    ]
  },
  {
    id: "can-ai-search-engines-crawl-understand-cite-website",
    slug: "/blog/can-ai-search-engines-crawl-understand-cite-website/",
    title: "Can AI Search Engines Crawl, Understand and Cite Your Website?",
    subtitle: "Before asking 'How do I get cited by AI?', start with: 'Can AI search engines actually access, understand, retrieve, and cite your content?' The complete technical checklist & 4-layer AI visibility framework.",
    category: "SEO & Technical SEO",
    date: "Sep 20, 2026",
    readTime: "11 min read",
    badge: "Signature Technical Playbook",
    featured: false,
    summary:
      "You have published great content. Your website is technically optimized. Your pages are indexed by Google. But can AI search engines actually access, understand, retrieve, and cite your content?",
    content: [
      {
        heading: "Can AI Search Engines Access, Understand, Retrieve, and Cite Your Content?",
        text: `You have published great content.

Your website is technically optimized.

Your pages are indexed by Google.

But there is another question you should be asking:

“Can AI search engines actually access, understand, retrieve, and cite your content?”

As search evolves from traditional blue links to AI-generated answers, this question is becoming increasingly important.

However, there is a common mistake I see in AI SEO.

We jump directly into:
• AI SEO
• AEO
• GEO
• LLM optimization
• entity optimization
• AI content strategies

without first checking whether the technical foundation allows machines to access the content in the first place.

Before asking “How do I get cited by AI?”, start with:
“Can the systems I care about actually access and understand my content?”

Here is the checklist I use.`
      },
      {
        heading: "The AI Content Accessibility Checklist",
        text: `Think about AI visibility as a journey:

ACCESS → DISCOVER → UNDERSTAND → RETRIEVE → CITE

If you have a problem at the first stage, optimizing the final stage becomes much harder.`
      },
      {
        heading: "1. Does Your Page Return a Successful HTTP Response?",
        text: `Start with the basics.

Your important pages should be accessible and return an appropriate HTTP status.

For a normal indexable webpage, you generally want the final URL to return: 200 OK

Check for:
• 404 errors
• 410 responses
• 403 Forbidden
• 5xx server errors
• unexpected redirect chains
• HTTP → HTTPS issues
• CDN or WAF blocking
• authentication requirements

A page can be perfectly optimized from a content perspective and still be inaccessible to a crawler because your infrastructure is returning a 403, challenging the bot with a CAPTCHA, or blocking automated traffic.

OpenAI's current crawler guidance, for example, specifically recommends checking robots.txt, WAF/CDN rules, bot mitigation, authentication, JavaScript challenges and rate limiting when its crawlers cannot access a page.

What to check:
URL
 ↓
HTTP request
 ↓
200 OK?
 ↓
Content accessible?

If the answer stops at the HTTP layer, there is no point discussing content optimization yet.`
      },
      {
        heading: "2. Is Your robots.txt Blocking Relevant AI Crawlers?",
        text: `Go to:
https://example.com/robots.txt

Then check whether your important content is being blocked.

For example:
User-agent: *
Disallow: /
is effectively telling compliant crawlers not to crawl the entire site.

But there is an important distinction here: Don't automatically allow every AI crawler.

Different crawlers can serve different purposes. Some are associated with search discovery, while others may serve different functions such as training, research or product experiences.

Your crawler policy should therefore reflect your publishing strategy.

For example, OpenAI currently documents OAI-SearchBot and states that publishers who want their content to be discoverable and surfaced in ChatGPT should avoid blocking it.

So instead of asking: “Have I allowed all AI bots?”
Ask: “Have I intentionally decided which AI crawlers should be allowed to access my content?”

That is a much better SEO question.`
      },
      {
        heading: "3. Is Your Page Actually Indexable?",
        text: `Crawling and indexing are not the same thing. A crawler may be able to access your page while the page is still prevented from being indexed.

Check for:
<meta name="robots" content="noindex">

Also check:
• X-Robots-Tag
• robots.txt
• canonical implementation
• authentication
• sitemap inclusion
• internal links
• Google Search Console indexability
• URL Inspection

Google's documentation recommends checking that pages are accessible and not blocked by robots.txt, noindex, or login requirements when validating how Google sees a page.

This leads to a simple principle:
“If you don't want a page discoverable through search, don't assume an AI system will treat it as a normal public source either.”

There are differences between individual AI systems, so indexing should not be treated as a universal prerequisite for every AI product. But for organic search visibility, it remains a fundamental technical check.`
      },
      {
        heading: "4. Is Your Canonical URL Correct?",
        text: `This is where a common SEO misconception needs correcting.
A canonical tag does not mean: “This is my most important page.”

Instead, canonicalization tells search engines which URL you consider the representative version when multiple URLs contain duplicate or substantially similar content.

For example:
<link rel="canonical" href="https://example.com/technical-seo/">

A self-referencing canonical is common for a standalone preferred URL. But don't add self-canonicals blindly.

Ask:
• Is this the preferred URL?
• Are there duplicate URLs?
• Are URL parameters creating variations?
• Are HTTP and HTTPS versions consolidated?
• Are localized URLs implemented correctly?
• Are trailing-slash variations handled consistently?
• Is JavaScript changing the canonical incorrectly?

Google's current documentation specifically highlights the importance of making canonicalization clear in the original HTML, especially when JavaScript is involved.

So the question isn't: “Does every page have a self-canonical?”
The better question is: “Have I clearly communicated which URL represents this content?”`
      },
      {
        heading: "5. Can Search Engines and AI Systems Discover Your Important Pages Through Internal Links?",
        text: `A page can exist on your website and still be difficult to discover.

Look at your internal linking structure. Your important pages should be connected logically to other relevant pages.

For example:
SEO
│
├── Technical SEO
│   ├── Crawlability
│   ├── Indexability
│   └── JavaScript SEO
│
├── Content SEO
│   ├── Search Intent
│   ├── Keyword Research
│   └── Content Optimization
│
└── AI Search Optimization
    ├── AI Crawlers
    ├── AI Visibility
    └── AI Citations

This creates contextual relationships between your content.

But there is another common mistake: Don't force keywords into every anchor text.

Your internal links should use descriptive anchor text that accurately describes the destination.

For example:
Good: “Learn more about JavaScript SEO”
Weak: “Click here”

The objective isn't to create keyword-stuffed anchors.
The objective is to create clear relationships between relevant pieces of content.`
      },
      {
        heading: "6. Can Your Important Content Be Accessed Without Depending Entirely on Client-Side JavaScript?",
        text: `This topic needs some nuance.
You may have heard: “AI bots can't read JavaScript.”
That is too simplistic.

Google has been rendering JavaScript for years, and its current documentation explicitly notes that JavaScript itself does not automatically make content inaccessible to Google Search.

However, that doesn't mean you should make important content unnecessarily dependent on complex client-side rendering.

Check:
• Is the main content present?
• Are headings available?
• Are important links available?
• Is content loaded through an API?
• Does content appear only after user interaction?
• Does the content disappear when JavaScript fails?
• Are there rendering errors?
• Are important sections hidden behind client-side components?

The principle is simple:
“If the content is important, make sure it is reliably accessible to crawlers and renderers.”`
      },
      {
        heading: "7. Is Your Structured Data Helping Machines Understand the Page?",
        text: `Structured data can provide additional machine-readable context about your content.
Depending on the page, this could include:
• Article
• BreadcrumbList
• Organization
• Person
• Product
• ProfilePage
• other relevant Schema.org types

For example:
{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Can AI Search Engines Crawl, Understand and Cite Your Website?",
  "author": {
    "@type": "Person",
    "name": "Your Name"
  }
}

But remember:
“Structured data is not a replacement for your actual content.”

Don't put valuable information only inside JSON-LD and assume that adding schema makes the information available to AI systems.

Google describes structured data as a standardized format for providing information about and classifying page content. It also recommends validating structured data and using URL Inspection to test how Google sees the page.

Your visible content still matters.`
      },
      {
        heading: "8. What Do Your Server Logs Tell You?",
        text: `This is one of the most underrated SEO checks.
Instead of assuming: “I allowed AI bots, so they must be crawling my website.”
Look at the evidence.

Your server logs can potentially show:
• User agent
• Requested URL
• HTTP status
• Response time
• Crawl frequency
• Response size
• Blocked requests
• Repeated errors

You might discover something like:
Crawler → /blog/article-1 → 200
Crawler → /blog/article-2 → 403
Crawler → /blog/article-3 → 200
Crawler → /blog/article-4 → 500

That gives you a much clearer picture of what your infrastructure is actually serving.

But there is an important limitation:
“A crawler request does not prove that an AI system understood, indexed, retrieved or cited your content.”
It only proves that a request was made.

That distinction is extremely important when measuring AI visibility.`
      },
      {
        heading: "The 30-Second AI Retrieval Sanity Check",
        text: `Now for a simple test you can perform yourself.

Find a distinctive passage from the page you want to test. Take approximately 30–40 words.

Then ask an AI system:
“Find this exact content on the internet and give me the URL of the page where it appears.”

For example, if your website contains:
“Hybrid Engine Optimization combines traditional search optimization with answer-oriented optimization to improve visibility across search engines and AI-powered discovery systems.”

Copy a distinctive section and run the test.

If the AI returns your URL:
You have evidence that the content is discoverable through that particular system at that moment.

If it doesn't return your URL:
Don't immediately conclude that: “The AI can't read my website.”
There could be several reasons. The result can depend on the AI system, retrieval technology, search index freshness, query wording, geographic availability, crawl freshness, whether the page has been discovered, or whether another website has reproduced similar content.

So treat this as a:
Retrieval sanity check — not a definitive AI crawlability test.`
      },
      {
        heading: "But Here's the Important Part",
        text: `Finding your URL does not automatically mean:
• the AI has permanently indexed your page
• the AI crawler directly visited your page
• your page will be cited for relevant questions
• the AI understands your entire article
• your website has strong AI visibility

Likewise, not finding your URL does not automatically mean your website is technically inaccessible.

This is why I separate AI visibility into four stages.`
      },
      {
        heading: "The Four Layers of AI Visibility",
        text: `Layer 1 — Accessibility
Can the system reach your content?
Check: HTTP status, HTTPS, robots.txt, WAF/CDN, authentication, bot protection
↓
Layer 2 — Discoverability
Can the system discover your content?
Check: internal links, XML sitemap, crawl paths, canonicalization, external references
↓
Layer 3 — Understanding
Can the system clearly understand what your content is about?
Check: page structure, headings, semantic relationships, structured data, entities, clear explanations, visible content
↓
Layer 4 — Retrieval & Citation
Does the system retrieve your page when users ask relevant questions?
Check: branded queries, informational queries, question-based queries, citation frequency, URL visibility, source selection

This is where many AI SEO strategies jump too quickly.
They start at Layer 4 without validating Layers 1–3.`
      },
      {
        heading: "What If Your Website Is Crawlable but Still Isn't Being Cited?",
        text: `This is where the problem becomes more interesting.
Suppose you have already confirmed:
200 OK → Crawlable → Indexable → Correct canonical → Internally linked → Content accessible

But your pages still don't appear as sources in AI answers.
Then the problem may no longer be purely technical.

Start investigating:
1. Topical relevance: Does your page actually answer the question being asked?
2. Information completeness: Does it provide a complete answer, or does the user need to visit five other pages?
3. Content structure: Can important facts and concepts be extracted easily?
4. Entity clarity: Is it obvious who created the content and what the page is about?
5. Evidence: Are important claims supported by data, examples, original research or credible references?
6. External references: Are other relevant websites mentioning, referencing or citing your brand?
7. Original value: Does your page provide something genuinely useful that isn't simply repeating the same information available everywhere else?

This is where technical SEO starts connecting with AEO, GEO and broader AI visibility optimization.`
      },
      {
        heading: "The Real Question Isn't “Can I Rank in AI?”",
        text: `It is:
“Can AI systems access, understand and retrieve my content when it is relevant to a user's question?”

That changes how you approach AI SEO.
Instead of immediately producing hundreds of AI-focused articles, start by validating the technical foundation.

Crawl → Access → Discover → Understand → Retrieve → Cite

Technical SEO removes barriers. Content quality and relevance create value. External signals can provide additional context. And ultimately, retrieval systems decide which sources are surfaced for a particular query.

There is no single technical setting that guarantees an AI citation.`
      },
      {
        heading: "Final Takeaway",
        text: `AI visibility doesn't start with AI.
It starts with your website.

Before asking: “How do I get ChatGPT to cite my website?”, ask:
“Can the systems I care about actually access, understand and retrieve what I've published?”

If the answer is no, fix the technical foundation first.
If the answer is yes, then move to the next layer:

Relevance → Content quality → Entity clarity → Evidence → Authority → Retrieval → Citation

That's where AI visibility becomes more than just another SEO checklist.
And that's where SEO, AEO and emerging AI-search optimization strategies start coming together.`
      }
    ]
  }
];

const CATEGORIES = [
  "All",
  "AEO/GEO , AI Visibility",
  "SEO & Technical SEO"
];

export default function BlogHomePage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeArticle, setActiveArticle] = useState(null);
  const [copiedId, setCopiedId] = useState(false);

  useEffect(() => {
    const originalTitle = document.title;
    const blogTitle = "AI Search & Technical SEO Insights & Playbooks | Kanna Narendra Kumar";
    document.title = blogTitle;

    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    canonical.setAttribute("href", "https://narendradigital.in/blog/");

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
    };
  }, []);

  // Filtered articles
  const filteredArticles = useMemo(() => {
    return ARTICLES.filter((art) => {
      let matchesCat = false;
      if (selectedCategory === "All") {
        matchesCat = true;
      } else if (selectedCategory === "AEO/GEO , AI Visibility") {
        matchesCat =
          art.category.includes("AEO") ||
          art.category.includes("GEO") ||
          art.category.includes("AI Visibility");
      } else if (selectedCategory === "AEO & GEO") {
        matchesCat = art.category.includes("AEO") || art.category.includes("GEO");
      } else {
        matchesCat = art.category === selectedCategory;
      }

      const query = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !query ||
        art.title.toLowerCase().includes(query) ||
        art.subtitle.toLowerCase().includes(query) ||
        art.summary.toLowerCase().includes(query);
      return matchesCat && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  const featuredArticle = ARTICLES.find((a) => a.featured) || ARTICLES[0];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#070a12] text-white selection:bg-cyan-500 selection:text-black">
      {/* Top Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[360px] bg-indigo-600/10 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute top-60 right-10 w-[500px] h-[300px] bg-cyan-600/10 rounded-full blur-[140px] pointer-events-none" />

      {/* Main Global Navigation Bar (Original Site Navbar) */}
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 space-y-12 relative z-10">
        {/* Author / Hero Card featuring Narendra's Workstation Photo */}
        <section className="p-6 sm:p-8 lg:p-10 rounded-3xl bg-gradient-to-br from-[#0c1324] via-[#090d1a] to-[#060913] border border-white/10 shadow-2xl relative overflow-hidden">
          {/* Subtle Studio Ambient Glows */}
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[130px] pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-[130px] pointer-events-none" />

          <div className="flex flex-col md:flex-row items-center gap-8 lg:gap-10 relative z-10">
            {/* Workstation Photo Container - Compact Author-Scale Presentation (682x1024) */}
            <div className="shrink-0 flex justify-center">
              <div className="relative w-48 sm:w-52 md:w-56 lg:w-60 group">
                {/* Subtle ambient back-glow */}
                <div className="absolute -inset-1 bg-gradient-to-tr from-cyan-500/25 via-indigo-500/15 to-amber-500/25 rounded-2xl blur-md opacity-70 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                {/* Image Frame tailored to 682x1024 */}
                <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-[#0a0f1d] shadow-xl aspect-[682/1024]">
                  <img
                    src="/narendra-workstation.jpg"
                    alt="Kanna Narendra Kumar - Senior SEO Specialist & AI Search Strategist"
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="eager"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/narendra-avatar.jpg";
                    }}
                  />

                  {/* Subtle glass reflection overlay at bottom */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070a12]/80 via-transparent to-transparent pointer-events-none" />

                  {/* Floating Author Tag */}
                  <div className="absolute bottom-2.5 left-2.5 right-2.5 p-2 rounded-xl bg-[#080d1a]/90 backdrop-blur-md border border-white/10 flex items-center justify-between text-xs shadow-lg">
                    <div className="truncate pr-1">
                      <p className="font-heading font-bold text-white text-[11px] leading-tight truncate">Narendra Kumar</p>
                      <p className="text-[9px] font-mono text-cyan-300 truncate">SEO & Growth Specialist</p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-mono text-emerald-400 font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Copy & Domain Highlights */}
            <div className="flex-1 space-y-5 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-widest text-cyan-400">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Field Notes & Tactical Playbooks</span>
              </div>

              <h1 className="font-heading font-black text-3xl sm:text-4xl xl:text-5xl text-white tracking-tight uppercase leading-tight">
                The Search & <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400">Digital Growth Lab</span>
              </h1>

              <p className="text-sm sm:text-base text-white/70 leading-relaxed font-normal max-w-2xl mx-auto md:mx-0">
                Tactical playbooks, architectural teardowns, and actionable guides across <strong className="text-white">SEO, AEO, GEO, Technical SEO, and Growth Marketing</strong>. Engineered to dominate both traditional Google search rankings and generative AI answer platforms.
              </p>

              {/* 4 Core Focus Areas */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-left">
                  <div className="text-[10px] font-mono uppercase text-cyan-400 font-bold mb-0.5">Focus 01</div>
                  <div className="text-xs font-heading font-bold text-white">Technical SEO</div>
                  <div className="text-[10px] text-white/50 mt-0.5">Crawl Architecture & Migrations</div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-left">
                  <div className="text-[10px] font-mono uppercase text-indigo-400 font-bold mb-0.5">Focus 02</div>
                  <div className="text-xs font-heading font-bold text-white">AEO & AI Search</div>
                  <div className="text-[10px] text-white/50 mt-0.5">Claude & ChatGPT Search</div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-left">
                  <div className="text-[10px] font-mono uppercase text-purple-400 font-bold mb-0.5">Focus 03</div>
                  <div className="text-xs font-heading font-bold text-white">GEO Frameworks</div>
                  <div className="text-[10px] text-white/50 mt-0.5">Information Gain & LLM Retrieval</div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-left">
                  <div className="text-[10px] font-mono uppercase text-emerald-400 font-bold mb-0.5">Focus 04</div>
                  <div className="text-xs font-heading font-bold text-white">Growth Marketing</div>
                  <div className="text-[10px] text-white/50 mt-0.5">B2B SaaS Pipeline Scaling</div>
                </div>
              </div>

              {/* Author Meta Details */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 pt-1 text-xs text-white/60 font-mono">
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4 text-cyan-400" />
                  <span>By Kanna Narendra Kumar</span>
                </span>
                <span className="hidden sm:inline">&bull;</span>
                <span className="flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4 text-indigo-400" />
                  <span>{ARTICLES.length} Deep-Dive Playbooks</span>
                </span>
                <span className="hidden sm:inline">&bull;</span>
                <span className="flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span>Updated for 2026</span>
                </span>
                <Link
                  to="/ai-visibility-audit/"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white font-bold transition-all"
                >
                  <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Get an AI Visibility Audit</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>


        {/* Search & Topic Filter Bar */}
        <section className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 rounded-2xl bg-[#0c1222] border border-white/10 shadow-xl">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tactical articles by keyword, framework, or topic..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white placeholder-white/30 text-xs sm:text-sm focus:outline-none focus:border-cyan-400 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white text-xs"
              >
                Clear
              </button>
            )}
          </div>

          {/* Categories */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-2 rounded-xl text-xs font-mono whitespace-nowrap transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? "bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-600/30"
                    : "bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Featured Post Spotlight (When All selected and no search) */}
        {selectedCategory === "All" && !searchQuery && featuredArticle && (
          <section className="p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#0c1222] via-[#0e172d] to-[#0c1222] border border-indigo-500/30 shadow-2xl relative group">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-2.5">
                  <span className="px-3 py-1 rounded-full text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider">
                    {featuredArticle.badge}
                  </span>
                  <span className="text-xs text-white/40 font-mono flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {featuredArticle.readTime}
                  </span>
                  <span className="text-xs text-white/40 font-mono flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {featuredArticle.date}
                  </span>
                </div>

                <a 
                  href={featuredArticle.slug}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-heading font-black text-2xl sm:text-3xl text-white hover:text-cyan-400 transition-colors leading-tight tracking-tight block"
                >
                  {featuredArticle.title}
                </a>

                <p className="text-sm sm:text-base text-white/70 leading-relaxed font-normal">
                  {featuredArticle.summary}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <a
                  href={featuredArticle.slug}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-xl shadow-cyan-500/20 hover:scale-105 transition-all"
                >
                  <span>Read Full Article</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/ai-visibility-audit/"
                  className="px-5 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/35 text-cyan-300 hover:text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all"
                >
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  <span>Get AI Visibility Audit</span>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Articles Grid */}
        <section className="space-y-6">
          <div className="flex items-center justify-between pb-2 border-b border-white/10">
            <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              <span>
                {selectedCategory === "All" ? "All Publications" : `${selectedCategory} Articles`}
              </span>
              <span className="text-xs font-mono text-white/40 font-normal">
                ({filteredArticles.length})
              </span>
            </h3>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="p-12 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-3">
              <p className="text-sm font-mono text-white/50">No articles matched your search query.</p>
              <button
                onClick={() => {
                  setSelectedCategory("All");
                  setSearchQuery("");
                }}
                className="px-4 py-2 rounded-xl bg-white/[0.08] hover:bg-white/[0.15] text-xs font-mono text-white transition-colors"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredArticles.map((article) => {
                const articleUrl = article.slug || "/blog/can-ai-search-engines-crawl-understand-cite-website/";
                return (
                  <a
                    key={article.id}
                    href={articleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-6 sm:p-7 rounded-3xl bg-[#0c1222] border border-white/10 hover:border-cyan-500/40 transition-all duration-200 shadow-xl hover:shadow-2xl hover:shadow-cyan-950/20 flex flex-col justify-between group block text-left"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase tracking-wider">
                          {article.category}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-white/40 font-mono">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{article.readTime}</span>
                        </div>
                      </div>

                      <h4 className="font-heading font-bold text-lg sm:text-xl text-white group-hover:text-cyan-400 transition-colors leading-snug">
                        {article.title}
                      </h4>

                      <p className="text-xs sm:text-sm text-white/60 line-clamp-3 leading-relaxed font-normal">
                        {article.summary}
                      </p>
                    </div>

                    <div className="pt-5 mt-5 border-t border-white/[0.06] flex items-center justify-between text-xs">
                      <span className="text-white/40 font-mono">{article.date}</span>
                      <span className="font-semibold text-cyan-400 group-hover:translate-x-1 transition-transform flex items-center gap-1.5">
                        <span>Read Full Article</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </section>

        {/* Collaboration CTA Footer */}
        <section className="p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Cpu className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
            Need High-Impact Search & AI Execution?
          </h3>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Whether you are navigating Google AI Overviews, preparing a CMS migration, or scaling high-converting organic SQLs, let's connect directly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai-visibility-audit/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Schedule Strategy Discussion</span>
              <ArrowUpRight className="w-4 h-4 text-cyan-400" />
            </Link>
          </div>
        </section>
      </main>

      {/* ---------------------------------------------------- */}
      {/* ARTICLE READER MODAL                                 */}
      {/* ---------------------------------------------------- */}
      {activeArticle && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setActiveArticle(null)}
        >
          <div 
            className="bg-[#0c1222] border border-cyan-500/20 rounded-3xl max-w-3xl w-full p-6 sm:p-10 shadow-2xl relative max-h-[90vh] overflow-y-auto space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-white/10 pb-5">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="px-3 py-1 rounded-full text-[10px] font-mono font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase">
                    {activeArticle.category}
                  </span>
                  <span className="text-xs text-white/40 font-mono flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {activeArticle.readTime}
                  </span>
                  <span className="text-xs text-white/40 font-mono flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {activeArticle.date}
                  </span>
                </div>
                <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight leading-tight">
                  {activeArticle.title}
                </h2>
                <p className="text-xs sm:text-sm text-cyan-300 font-medium leading-relaxed">
                  {activeArticle.subtitle}
                </p>
              </div>

              <button
                onClick={() => setActiveArticle(null)}
                className="p-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors shrink-0 ml-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Article Content Sections */}
            <div className="space-y-8 py-2 text-white/80 leading-relaxed font-sans text-sm sm:text-base">
              {activeArticle.content.map((sec, idx) => (
                <div key={idx} className="space-y-3">
                  <h3 className="font-heading font-bold text-lg sm:text-xl text-white tracking-tight">
                    {sec.heading}
                  </h3>
                  <div className="text-white/75 text-sm sm:text-base leading-relaxed whitespace-pre-line bg-[#080d19] p-4 sm:p-5 rounded-2xl border border-white/[0.06]">
                    {sec.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Author Footer in Modal */}
            <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <img
                  src="/narendra-workstation.jpg"
                  alt="Kanna Narendra Kumar"
                  className="w-12 h-12 rounded-xl object-cover object-[center_20%] border border-cyan-500/30"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/narendra-avatar.jpg";
                  }}
                />
                <div>
                  <h5 className="font-heading font-bold text-sm text-white">
                    Written by Kanna Narendra Kumar
                  </h5>
                  <p className="text-[11px] text-white/50">
                    Senior SEO, AEO, GEO & Growth Marketing Specialist
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                {activeArticle.slug && (
                  <Link
                    to={activeArticle.slug}
                    onClick={() => setActiveArticle(null)}
                    className="px-3 py-2 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-xs font-mono text-cyan-300 flex items-center gap-1.5 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Full Page View</span>
                  </Link>
                )}
                <button
                  onClick={handleCopyLink}
                  className="px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 text-xs font-mono text-white/80 flex items-center gap-1.5 transition-colors"
                >
                  {copiedId ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
                  <span>{copiedId ? "Copied" : "Share"}</span>
                </button>
                <Link
                  to="/ai-visibility-audit/"
                  onClick={() => setActiveArticle(null)}
                  className="px-4 py-2 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Get AI Audit</span>
                </Link>
                <Link
                  to="/#contact"
                  onClick={() => setActiveArticle(null)}
                  className="px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider"
                >
                  Discuss Strategy
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ArrowRightIcon({ className }) {
  return (
    <svg 
      className={className} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
